/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Button, Card, CardActions, CardContent, CardHeader, IconButton, Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import {
  getIn, useFormikContext,
} from 'formik';
import React, {
  useEffect, useState,
} from 'react';

import GeneralFormFields from 'components/GeneralFormView/types';
import DangerIconButton from 'components/DangerIconButton';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';

// import IconButton from '@material-ui/core/IconButton';

import {
  generateInitialValues,
} from '../..';
import FieldError from '../../Utils/FieldError';
import FormFieldCondition from '../../Utils/FormFieldCondition';
import {
  ArrayFormFieldFieldValue, FieldPropsCommon,
} from '../types';
import {
  onChange,
} from '../utils';

import styles from '../styles.module.scss';

type Props<T, K extends keyof T> = FieldPropsCommon<T, K> & ArrayFormFieldFieldValue<T, K>;

const ArrayFormField = <FormModel, K extends keyof FormModel>(
  props: Props<FormModel, K>,
) => {
  const {
    values,
    handleChange,
    errors,
    touched,
    isSubmitting,
    setFieldTouched,
  } = useFormikContext<FormModel>();

  const {
    fieldOptions,
    disabled,
    title,
    location,
  } = props;

  const value: any[] = getIn(values, location);
  const onValueChange = onChange(handleChange, location);
  const error = getIn(errors, location);
  const isTouched = getIn(touched, location);

  useEffect(() => {
    if (isSubmitting) {
      if (!Array.isArray(error)) {
        setFieldTouched(location);
      }
    }
  }, [error, isSubmitting, location, setFieldTouched]);

  const remove = (index: number) => {
    const formValues = value;
    formValues.splice(index, 1);
    onValueChange(formValues);
  };

  const [isListExpanded, setListExpanded] = useState<boolean>(true);
  const [isItemExpanded, setItemExpanded] = useState<{[key: string]: boolean}>(
    {
    },
  );

  const handleExpandClick = (toCollapse: 'list' | 'item', index?: string) => {
    if (toCollapse === 'list') {
      setListExpanded(!isListExpanded);
    }
    if (toCollapse === 'item' && !!index) {
      setItemExpanded((prev) => ({
        [index]: !isItemExpanded[index],
      }));
    }
  };

  const swap = (index: number, direction: 'up' | 'down') => {
    const formValues = value;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = formValues[targetIndex];
    formValues[targetIndex] = formValues[index];
    handleExpandClick('item', `${targetIndex}`);
    document.getElementById(`${title}-${targetIndex}`)?.scrollIntoView({
      behavior: 'smooth',
    });
    formValues[index] = temp;
    onValueChange(formValues);
  };

  const notFirstLevel = () => (location.includes('['));

  const subHeaderKey = () => (fieldOptions.formData.type === GeneralFormFields.NESTED_FORM
    && fieldOptions.formData.fieldOptions?.titleColumn
    ? fieldOptions.formData.fieldOptions?.titleColumn : '');

  return (
    <Card
      style={{
        margin: 8,
        boxShadow: 'none',
        border: 'none',
        backgroundColor: 'transparent',
      }}
    >
      <CardHeader
        title={title}
        action={(
          <div>
            <Button
              variant="contained"
              disabled={disabled}
              color="primary"
              onClick={() => {
                setListExpanded(true);
                onValueChange([
                  ...value,
                  generateInitialValues(fieldOptions.formData as any),
                ]);
                if (!Array.isArray(error)) {
                  setFieldTouched(location);
                }
              }}
            >
              Add
            </Button>
            { fieldOptions.isCollapsible
              && (
                <IconButton
                  onClick={() => handleExpandClick('list')}
                  aria-expanded={isListExpanded}
                  style={{ marginLeft: 20 }}
                >
                  { isListExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon /> }
                </IconButton>
              )}
          </div>
        )}
      />
      <CardContent>
        { isListExpanded && (
          <>
            {
              !value?.length && (
                <Typography>No items yet. Press add to create a new item.</Typography>
              )
            }
            {
              value?.map((item, index) => (
                <Card
                  style={{
                    marginBottom: 8,
                  }}
                  className={notFirstLevel() ? styles.notFirstLevelcard : ''}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${index}`}
                  id={`${title}-${index}`}
                >
                  <CardHeader
                    title={`${title} ${index + 1}`}
                    subheader={subHeaderKey() ? item[subHeaderKey()] : ''}
                    action={
                      (
                        <>
                          <IconButton
                            color="primary"
                            disabled={index === 0 || disabled}
                            onClick={() => {
                              swap(index, 'up');
                            }}
                          >
                            <ArrowUpwardIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            color="primary"
                            disabled={(index === value?.length - 1) || disabled}
                            onClick={() => {
                              swap(index, 'down');
                            }}
                          >
                            <ArrowDownwardIcon fontSize="inherit" />
                          </IconButton>
                          <DangerIconButton
                            disabled={disabled}
                            onClick={() => {
                              remove(index);
                            }}
                          >
                            <DeleteIcon />
                          </DangerIconButton>
                          {
                              fieldOptions.formData.type === GeneralFormFields.NESTED_FORM
                              && fieldOptions.formData.fieldOptions.isCollapsibleInArray
                                ? (
                                  <IconButton
                                    onClick={() => handleExpandClick('item', `${index}`)}
                                    aria-expanded={isItemExpanded[index]}
                                  >
                                    { isItemExpanded[index] ? <ExpandLessIcon /> : <ExpandMoreIcon /> }
                                  </IconButton>
                                ) : null
                          }
                        </>
                      )
                    }
                  />
                  {
                    ((fieldOptions.formData.type === GeneralFormFields.NESTED_FORM
                    && !fieldOptions.formData.fieldOptions.isCollapsibleInArray)
                    || isItemExpanded[index])
                    && (
                      <CardContent
                        style={{
                          padding: 0,
                        }}
                      >
                        <FormFieldCondition<FormModel[K], keyof FormModel[K]>
                          fieldData={fieldOptions.formData as any}
                          location={`${location}[${index}]`}
                        />
                      </CardContent>
                    )
                  }
                </Card>
              ))
            }
          </>
        )}
        <FieldError
          errors={error}
          touched={isTouched}
        />
      </CardContent>
    </Card>
  );
};

export default ArrayFormField;
/* eslint-enable @typescript-eslint/no-explicit-any */
